import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, ChevronDown, ChevronRight, Search, ArrowUp, ArrowDown, ArrowUpDown, Info } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { formatRupeeOrDash as fmt, formatQtyOrDash as fmtQty } from '@/lib/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const DRIFT_THRESHOLD = 5; // %

function DriftChip({ pct }) {
  if (pct == null) return <span className="text-stone-400 text-xs">n/a</span>;
  const abs = Math.abs(pct);
  const color = abs <= DRIFT_THRESHOLD
    ? 'bg-stone-100 text-stone-600'
    : pct > 0
      ? 'bg-red-100 text-red-800'
      : 'bg-lime-100 text-lime-800';
  const sign = pct > 0 ? '+' : '';
  const icon = abs <= DRIFT_THRESHOLD ? '✓' : (pct > 0 ? '↑' : '↓');
  return (
    <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium ${color}`} data-testid="drift-chip">
      {icon} {sign}{pct}%
    </span>
  );
}

function SortHeader({ label, active, dir, onClick, align = 'left', testId }) {
  const Icon = !active ? ArrowUpDown : (dir === 'asc' ? ArrowUp : ArrowDown);
  return (
    <th
      data-testid={testId}
      onClick={onClick}
      className={`px-3 py-2 text-${align} text-[11px] uppercase tracking-widest text-stone-500 cursor-pointer select-none hover:bg-stone-100`}
    >
      <span className="inline-flex items-center gap-1">{label} <Icon className="w-3 h-3" /></span>
    </th>
  );
}

export default function Audit() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [sortKey, setSortKey] = useState('input_drift_pct');
  const [sortDir, setSortDir] = useState('desc');
  const [expanded, setExpanded] = useState({}); // {product_id: {loading, data, error}}

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/audit/price-drift`, { withCredentials: true })
      .then(r => setRows(r.data || []))
      .catch(e => setError(e?.response?.data?.detail || 'Failed to load audit report'))
      .finally(() => setLoading(false));
  }, []);

  const filteredSorted = useMemo(() => {
    const term = q.trim().toLowerCase();
    let list = rows;
    if (term) {
      list = list.filter(r =>
        (r.product_name || '').toLowerCase().includes(term) ||
        (r.product_brand || '').toLowerCase().includes(term) ||
        (r.product_size || '').toLowerCase().includes(term)
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      // nulls last regardless of direction
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') return av.localeCompare(bv) * dir;
      return (av - bv) * dir;
    });
  }, [rows, q, sortKey, sortDir]);

  const setSort = (k) => {
    if (k === sortKey) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(k);
      setSortDir(k.endsWith('_pct') ? 'desc' : 'asc');
    }
  };

  const toggleRow = async (pid) => {
    setExpanded(prev => {
      const cur = prev[pid];
      if (cur) {
        const { [pid]: _drop, ...rest } = prev;
        return rest;
      }
      return { ...prev, [pid]: { loading: true } };
    });
    if (!expanded[pid]) {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/audit/fifo-ledger/${pid}`, { withCredentials: true });
        setExpanded(prev => ({ ...prev, [pid]: { loading: false, data: r.data } }));
      } catch (e) {
        setExpanded(prev => ({ ...prev, [pid]: { loading: false, error: e?.response?.data?.detail || 'Failed to load ledger' } }));
      }
    }
  };

  if (loading) return <div className="p-8 text-stone-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Computing audit report…</div>;
  if (error) return <div className="p-8 text-red-700" data-testid="audit-error">{error}</div>;

  return (
    <div className="p-6 space-y-4" data-testid="audit-page">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-stone-900">Audit — Price Drift & FIFO Ledger</h1>
          <p className="text-sm text-stone-500 mt-1">
            Compare booked prices against actual recent purchases and sales. Click a row to see the FIFO chain of receipts, sales, and returns for that product.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            data-testid="audit-search"
            placeholder="Filter by product name, brand, size…"
            value={q}
            onChange={e => setQ(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="text-xs text-stone-500 flex items-center gap-1">
          <Info className="w-3.5 h-3.5" />
          {filteredSorted.length} product{filteredSorted.length === 1 ? '' : 's'} · drift threshold {DRIFT_THRESHOLD}%
        </div>
      </div>

      <div className="overflow-x-auto bg-white rounded-lg border border-stone-200">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr>
              <th className="w-8"></th>
              <SortHeader label="Product" active={sortKey === 'product_name'} dir={sortDir} onClick={() => setSort('product_name')} testId="sort-product" />
              <SortHeader label="Stock" active={sortKey === 'stock_on_hand'} dir={sortDir} onClick={() => setSort('stock_on_hand')} align="right" testId="sort-stock" />
              <SortHeader label="Booked cost" active={sortKey === 'booked_cost'} dir={sortDir} onClick={() => setSort('booked_cost')} align="right" testId="sort-bcost" />
              <SortHeader label="Actual paid" active={sortKey === 'actual_paid'} dir={sortDir} onClick={() => setSort('actual_paid')} align="right" testId="sort-apaid" />
              <SortHeader label="Input drift" active={sortKey === 'input_drift_pct'} dir={sortDir} onClick={() => setSort('input_drift_pct')} align="right" testId="sort-idrift" />
              <SortHeader label="Booked sell" active={sortKey === 'booked_sell'} dir={sortDir} onClick={() => setSort('booked_sell')} align="right" testId="sort-bsell" />
              <SortHeader label="Actual sold" active={sortKey === 'actual_sold'} dir={sortDir} onClick={() => setSort('actual_sold')} align="right" testId="sort-asold" />
              <SortHeader label="Sell drift" active={sortKey === 'sell_drift_pct'} dir={sortDir} onClick={() => setSort('sell_drift_pct')} align="right" testId="sort-sdrift" />
            </tr>
          </thead>
          <tbody>
            {filteredSorted.map((r) => (
              <React.Fragment key={r.product_id}>
                <tr
                  data-testid={`audit-row-${r.product_id}`}
                  onClick={() => toggleRow(r.product_id)}
                  className="border-b border-stone-100 hover:bg-amber-50/40 cursor-pointer"
                >
                  <td className="pl-3 text-stone-400">
                    {expanded[r.product_id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-stone-900">{r.product_name} {!r.is_active && <span className="text-[10px] text-stone-400 ml-1">(inactive)</span>}</div>
                    <div className="text-[11px] text-stone-500">{[r.product_brand, r.product_size, r.product_color].filter(Boolean).join(' · ')}</div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtQty(r.stock_on_hand)} {r.unit}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(r.booked_cost)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {fmt(r.actual_paid)}
                    <div className="text-[10px] text-stone-400">{r.po_receipts_used} PO{r.po_receipts_used === 1 ? '' : 's'}</div>
                  </td>
                  <td className="px-3 py-2 text-right"><DriftChip pct={r.input_drift_pct} /></td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(r.booked_sell)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {fmt(r.actual_sold)}
                    <div className="text-[10px] text-stone-400">{r.sales_used} sale{r.sales_used === 1 ? '' : 's'}</div>
                  </td>
                  <td className="px-3 py-2 text-right"><DriftChip pct={r.sell_drift_pct} /></td>
                </tr>
                {expanded[r.product_id] && (
                  <tr>
                    <td colSpan="9" className="bg-stone-50 border-b border-stone-200 p-4">
                      <LedgerPanel state={expanded[r.product_id]} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {filteredSorted.length === 0 && (
              <tr><td colSpan="9" className="px-3 py-8 text-center text-stone-400">No products match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LedgerPanel({ state }) {
  if (state.loading) return <div className="text-stone-500 flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading FIFO ledger…</div>;
  if (state.error) return <div className="text-red-700 text-sm">{state.error}</div>;
  const { summary, rows } = state.data || {};
  if (!summary) return null;
  return (
    <div className="space-y-3">
      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-xs">
        <SumCell label="Stock sold (all time)" value={`${fmtQty(summary.stock_sales_qty)} ${summary.unit}`} />
        <SumCell label="Revenue" value={fmt(summary.total_revenue)} />
        <SumCell label="Booked cost" value={fmt(summary.total_booked_cost)} />
        <SumCell label="Actual cost (FIFO)" value={fmt(summary.total_actual_cost)} />
        <SumCell label="Booked margin" value={fmt(summary.total_booked_margin)} />
        <SumCell
          label="Windfall (books − actual)"
          value={fmt(summary.total_windfall)}
          highlight={Math.abs(summary.total_windfall) > 0.5}
          positive={summary.total_windfall > 0}
        />
      </div>

      {/* Ledger table */}
      <div className="bg-white border border-stone-200 rounded overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-stone-100 text-stone-600 uppercase tracking-widest text-[10px]">
            <tr>
              <th className="text-left px-2 py-1.5">Date</th>
              <th className="text-left px-2 py-1.5">Event</th>
              <th className="text-left px-2 py-1.5">Reference</th>
              <th className="text-right px-2 py-1.5">Qty</th>
              <th className="text-right px-2 py-1.5">Unit</th>
              <th className="text-right px-2 py-1.5">Value</th>
              <th className="text-left px-2 py-1.5">Sourced from</th>
              <th className="text-right px-2 py-1.5">Booked margin</th>
              <th className="text-right px-2 py-1.5">Actual margin</th>
              <th className="text-right px-2 py-1.5">Windfall</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((r, idx) => <LedgerRow key={idx} r={r} unit={summary.unit} />)}
            {(!rows || rows.length === 0) && (
              <tr><td colSpan="10" className="px-2 py-6 text-center text-stone-400">No receipts, sales, or returns yet for this product.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SumCell({ label, value, highlight, positive }) {
  const cls = highlight
    ? (positive ? 'bg-lime-50 border-lime-200 text-lime-900' : 'bg-red-50 border-red-200 text-red-900')
    : 'bg-white border-stone-200 text-stone-800';
  return (
    <div className={`rounded border ${cls} px-3 py-2`}>
      <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
      <div className="font-semibold tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

function LedgerRow({ r, unit }) {
  const isSale = r.kind === 'sale';
  const isReceipt = r.kind === 'receipt_po';
  const isReturn = r.kind === 'return_restock';
  const kindLabel = isReceipt ? 'PO Receipt' : isReturn ? 'Return (Restock)' : 'Sale';
  const kindClass = isReceipt ? 'text-lime-800 bg-lime-50' : isReturn ? 'text-blue-800 bg-blue-50' : 'text-amber-900 bg-amber-50';

  return (
    <tr className="border-b border-stone-100">
      <td className="px-2 py-1.5 whitespace-nowrap">{fmtDate(r.date)}</td>
      <td className="px-2 py-1.5"><span className={`inline-block rounded px-2 py-0.5 text-[10px] font-medium ${kindClass}`}>{kindLabel}</span></td>
      <td className="px-2 py-1.5 text-stone-700">
        {isSale && <>Sold to <span className="font-medium">{r.customer_name || '—'}</span><div className="text-[10px] text-stone-400">{r.ref_id}{!r.from_stock && ' · vendor-routed'}</div></>}
        {isReceipt && <>From <span className="font-medium">{r.vendor_name || r.vendor_id || '—'}</span><div className="text-[10px] text-stone-400">{r.ref_id}</div></>}
        {isReturn && <>Customer return<div className="text-[10px] text-stone-400">{r.ref_id}</div></>}
      </td>
      <td className={`px-2 py-1.5 text-right tabular-nums ${r.qty_delta < 0 ? 'text-amber-900' : 'text-lime-800'}`}>
        {r.qty_delta > 0 ? '+' : ''}{fmtQty(r.qty_delta)} {unit}
      </td>
      <td className="px-2 py-1.5 text-right tabular-nums text-stone-600">
        {isSale ? fmt(r.unit_sell) : fmt(r.unit_cost)}
      </td>
      <td className="px-2 py-1.5 text-right tabular-nums font-medium">{fmt(r.line_value)}</td>
      <td className="px-2 py-1.5 text-[10px] text-stone-600">
        {isSale && r.sources && r.sources.map((s, i) => (
          <div key={i}>
            {s.source_kind === 'vendor_direct' ? 'Direct (vendor)' : s.source_kind === 'unbacked' ? '⚠ unbacked' : s.ref_id}
            <span className="text-stone-400"> · {fmtQty(s.qty)} @ {fmt(s.unit_cost)}</span>
          </div>
        ))}
        {!isSale && '—'}
      </td>
      <td className="px-2 py-1.5 text-right tabular-nums">{isSale ? fmt(r.booked_margin) : '—'}</td>
      <td className="px-2 py-1.5 text-right tabular-nums font-medium">{isSale ? fmt(r.actual_margin) : '—'}</td>
      <td className={`px-2 py-1.5 text-right tabular-nums font-medium ${isSale && Math.abs(r.windfall) > 0.5 ? (r.windfall > 0 ? 'text-lime-800' : 'text-red-700') : ''}`}>
        {isSale ? fmt(r.windfall) : '—'}
      </td>
    </tr>
  );
}
